import { Badge } from '@art-of-hospitality-solutions-llc/ui-kit/components/badge';
import { Heading } from '@art-of-hospitality-solutions-llc/ui-kit/components/heading';
import {
  Card,
  CardContent,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/card';
import { Link, useParams } from 'react-router-dom';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { useGetPurchase } from '@Hooks/usePurchases';
import { Purchase } from '@Types/purchase';
import BreadcrumbWrapper from '@Components/BreadcrumbWrapper';
import PurchaseReceipt from '@Components/PurchaseReceipt';
import { formatPhoneNumber, formatCurrency } from '@Utils/utils';
import { getMemberProfile } from '@Utils/fish';

const getCustomerName = (purchase?: Purchase): string => {
  if (!purchase?.fish) return 'Unknown';

  const profile = getMemberProfile(purchase.fish);
  if (profile) {
    return `${profile.fName} ${profile.lName}`;
  }

  const firstProfile = purchase.fish.fishProfiles?.[0];
  if (firstProfile) {
    return `${firstProfile.fName} ${firstProfile.lName}`;
  }

  return 'Unknown';
};

export default function TicketPurchaseDetails() {
  const { purchaseId } = useParams();
  const { data: purchase, isLoading } = useGetPurchase(purchaseId, {
    'include':
      'fish.fishProfiles,ticketedEventTickets.ticketType,ticketedEventTickets.event.venue,redemptions.promoCode',
    'filter[ticketedEventTickets][some][deletedAt]': 'null',
  });

  const customerName = useMemo(() => {
    if (!purchase) {
      return '';
    }

    return getCustomerName(purchase);
  }, [purchase]);

  const tickets = purchase?.ticketedEventTickets ?? [];

  const aggregatedTickets = useMemo(() => {
    if (!purchase) {
      return [];
    }
    const ticketMap = new Map<
      string,
      {
        ticketTypeId: string;
        ticketTypeName: string;
        quantity: number;
        price: number;
      }
    >();

    tickets.forEach((ticket) => {
      const typeId = ticket.ticketTypeId;
      const typeName = ticket.ticketType?.name ?? '-';
      const price = ticket.ticketType?.price ?? 0;

      if (ticketMap.has(typeId)) {
        const existing = ticketMap.get(typeId)!;
        existing.quantity += 1;
      } else {
        ticketMap.set(typeId, {
          ticketTypeId: typeId,
          ticketTypeName: typeName,
          quantity: 1,
          price: price,
        });
      }
    });

    return Array.from(ticketMap.values());
  }, [tickets, purchase]);

  if (isLoading || !purchase) {
    return <div>Loading...</div>;
  }

  const event = tickets[0]?.event;

  return (
    <div className="w-full">
      <BreadcrumbWrapper
        crumbArray={[
          { label: 'Tickets', path: '/events/tickets' },
          {
            label: purchase.id,
            path: `/events/tickets/purchases/${purchaseId}`,
          },
        ]}
      />

      <div className="mt-8 mb-4 flex items-end">
        <Heading>
          {customerName}
          {event?.name ? ` - ${event.name}` : ' - Ticket Purchase'}
        </Heading>
        <p className="mx-2 mb-1 text-sm font-bold text-zinc-500">
          TICKET PURCHASE
        </p>
      </div>

      <div className="grid grid-cols-1 gap-8 xl:grid-cols-3">
        <div className="flex flex-col space-y-8 xl:col-span-2">
          <div>
            <div className="mb-2 flex items-center justify-between">
              <Heading level="h3">Purchase Details</Heading>
            </div>
            <Card>
              <CardContent>
                <div className="grid grid-cols-2 gap-8">
                  <div className="flex flex-col space-y-4">
                    <div>
                      <div className="text-sm font-bold text-zinc-500">
                        Status
                      </div>
                      <Badge variant="secondary">{purchase.status}</Badge>
                    </div>
                    <div>
                      <div className="text-sm font-bold text-zinc-500">
                        Confirmation Number
                      </div>
                      <div>{purchase.confirmationNumber || '-'}</div>
                    </div>
                  </div>
                  <div className="flex flex-col space-y-4">
                    <div>
                      <div className="text-sm font-bold text-zinc-500">
                        Created At
                      </div>
                      <div>
                        {purchase.createdAt
                          ? DateTime.fromISO(purchase.createdAt).toLocaleString(
                              DateTime.DATETIME_MED,
                            )
                          : '-'}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-bold text-zinc-500">
                        Updated At
                      </div>
                      <div>
                        {purchase.updatedAt
                          ? DateTime.fromISO(purchase.updatedAt).toLocaleString(
                              DateTime.DATETIME_MED,
                            )
                          : '-'}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <Heading level="h3">Ticket Details</Heading>
            </div>
            <Card>
              <CardContent>
                <div className="space-y-4">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="pb-2 text-left text-sm font-bold text-zinc-500">
                            Ticket Type
                          </th>
                          <th className="pb-2 text-right text-sm font-bold text-zinc-500">
                            Quantity
                          </th>
                          <th className="pb-2 text-right text-sm font-bold text-zinc-500">
                            Price
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {aggregatedTickets.map((ticket) => (
                          <tr
                            key={ticket.ticketTypeId}
                            className="border-b last:border-0"
                          >
                            <td className="py-2 text-sm">
                              {ticket.ticketTypeName}
                            </td>
                            <td className="py-2 text-right text-sm">
                              {ticket.quantity}
                            </td>
                            <td className="py-2 text-right text-sm">
                              {formatCurrency(ticket.price)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {purchase.order?.checks?.[0] && (
            <div>
              <div className="mb-2 flex items-center justify-between">
                <Heading level="h3">Receipt</Heading>
              </div>
              <PurchaseReceipt check={purchase.order.checks[0]} />
            </div>
          )}


        </div>

        <div className="flex flex-col space-y-8">
          <div>
            <div className="mb-2 flex items-center justify-between">
              <Heading level="h3">Customer</Heading>
            </div>
            <Card>
              <CardContent>
                {purchase.fishId ? (
                  <div className="flex flex-col space-y-2">
                    <div>
                      <div className="text-sm font-bold text-zinc-500">
                        Name
                      </div>
                      <Link
                        to={`/guests/${purchase.fishId}`}
                        className="text-blue-600 hover:underline"
                      >
                        {customerName}
                      </Link>
                    </div>
                    {purchase.fish?.phoneNumber && (
                      <div>
                        <div className="text-sm font-bold text-zinc-500">
                          Phone
                        </div>
                        <div>
                          {formatPhoneNumber(
                            purchase.fish.phoneNumber.slice(2),
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-sm text-zinc-500">
                    No customer associated
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {event && (
            <div>
              <div className="mb-2 flex items-center justify-between">
                <Heading level="h3">Event</Heading>
              </div>
              <Card>
                <CardContent>
                  <div className="flex flex-col space-y-2">
                    <div>
                      <div className="text-sm font-bold text-zinc-500">
                        Event Name
                      </div>
                      <Link
                        to={`/ticketed-events/${event.id}`}
                        className="text-blue-600 hover:underline"
                      >
                        {event.name}
                      </Link>
                    </div>
                    {event.venue && (
                      <div>
                        <div className="text-sm font-bold text-zinc-500">
                          Venue
                        </div>
                        <div>{event.venue.name}</div>
                      </div>
                    )}
                    {event.startDateTime && (
                      <div>
                        <div className="text-sm font-bold text-zinc-500">
                          Start Date
                        </div>
                        <div>
                          {DateTime.fromISO(event.startDateTime).toLocaleString(
                            DateTime.DATETIME_MED,
                          )}
                        </div>
                      </div>
                    )}
                    {event.endDateTime && (
                      <div>
                        <div className="text-sm font-bold text-zinc-500">
                          End Date
                        </div>
                        <div>
                          {DateTime.fromISO(event.endDateTime).toLocaleString(
                            DateTime.DATETIME_MED,
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
